<template>
  <div class="flex flex-col lg:flex-row min-h-screen">
    <div class="w-full lg:w-[47%] bg-white flex flex-col">
      <div class="p-4 lg:p-8">
        <NuxtLink
          to="/print"
          class="text-primary flex items-center gap-2"
          @click.prevent="handleBackToPrint"
        >
          <Icon :icon="ArrowLeftSvg" />
          {{ t('print.back-to-print-store') }}
        </NuxtLink>
      </div>

      <div class="flex-1 flex flex-col px-4 lg:px-8">
        <div
          v-if="printBudgetSummary && printBudgetSummary.totalBalance > 0"
          class="mb-6"
        >
          <h3 class="text-lg font-medium mb-3 text-gray-900">Budget Summary</h3>

          <div class="p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <div class="grid grid-cols-3 divide-x divide-gray-300">
              <div class="text-center px-4">
                <div class="text-xl font-bold text-blue-900">
                  ${{ (printBudgetSummary.totalBalance / 100).toFixed(2) }}
                </div>
                <div class="text-sm text-gray-500 mt-1">
                  Print Budget Balance
                </div>
              </div>
              <div class="text-center px-4">
                <div class="text-xl font-bold text-blue-600">
                  ${{ orderTotal.toFixed(2) }}
                </div>
                <div class="text-sm text-gray-500 mt-1">
                  Today's Budget Spend
                </div>
              </div>
              <div class="text-center px-4">
                <div
                  class="text-xl font-bold"
                  :class="isBudgetExceeded ? 'text-[red]' : 'text-blue-900'"
                >
                  ${{ (budgetRemaining / 100).toFixed(2) }}
                </div>
                <div class="text-sm text-gray-500 mt-1">Budget Remaining</div>
              </div>
            </div>
          </div>
          <div v-if="isBudgetExceeded" class="p-4 rounded-lg">
            <div class="flex items-center">
              <span class="text-xs text-[red] font-bold">
                Your order total exceeds your available budget. Please adjust
                your order or contact your administrator for assistance.
              </span>
            </div>
          </div>
        </div>

        <h2 class="text-xl font-semibold mb-4">
          {{ t('print.order-summary') }}
        </h2>

        <div class="space-y-4">
          <div
            v-for="(item, key) in printStore.selectedItems"
            :key="key"
            class="p-6 bg-white rounded-2xl flex gap-4"
          >
            <img
              :src="item.previewUrl || item.imageUrl"
              class="w-32 h-32 object-cover rounded"
              alt=""
            />
            <div class="flex-1">
              <div>
                <div class="flex items-center gap-2">
                  <h3 class="text-2xl font-medium">{{ item.title }}</h3>
                  <div
                    v-if="item.isPersonalized"
                    class="px-2 py-1 border border-gray rounded-md"
                  >
                    <span class="text-xs font-medium text-blue-700"
                      >Personalized</span
                    >
                  </div>
                </div>

                <div class="space-y-1 mt-2">
                  <p class="text-[gray] font-medium">
                    {{ item.printProduct.name }}
                  </p>
                  <p class="text-[gray]">
                    {{ item.printProduct.mediumName }}
                  </p>
                  <p v-if="item.productType === 'PRINT'" class="text-[gray]">
                    Dimensions: {{ item.printProduct.dimensions }}
                  </p>
                  <p v-if="item.productType === 'PRODUCT'" class="text-[gray]">
                    Description: {{ item.description }}
                  </p>
                </div>

                <p
                  v-if="printStore.checkout.step >= 2 || paymentSuccessful"
                  class="text-[gray] mt-2"
                >
                  QTY: {{ printStore.quantities[key] }}
                </p>
              </div>
            </div>

            <div
              v-if="printStore.checkout.step === 1 && !paymentSuccessful"
              class="flex items-center gap-2 mt-4"
            >
              <div class="flex flex-col relative">
                <SelectInput
                  v-model="printStore.quantities[key]"
                  :options="getQuantityOptions(item)"
                  placeholder="Select quantity"
                  option-value-name="quantity"
                  option-label-name="displayText"
                  class="w-[200px] print-select"
                  @update:model-value="
                    () => updateQuantity(key, printStore.quantities[key])
                  "
                >
                  <template #option="{ option }">
                    <div class="flex justify-between w-full">
                      <span class="font-medium">
                        {{
                          option.quantity
                            ? option.displayText
                            : 'Clear Selection'
                        }}
                      </span>
                      <span v-if="option.quantity" class="text-primary">
                        {{ printStore.currencySymbol
                        }}{{
                          (option.quantity * option.pricePerUnit).toFixed(2)
                        }}
                      </span>
                    </div>
                  </template>
                </SelectInput>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-8 space-y-4 border-t border-gray-200 pt-4">
          <div class="flex justify-between items-center">
            <span class="text-[#6B7280] text-md">{{
              t('print.subtotal')
            }}</span>
            <span class="text-md"
              >{{ printStore.currencySymbol
              }}{{ printStore.subtotal.toFixed(2) }}</span
            >
          </div>

          <template v-if="printStore.checkout.step === 3">
            <div class="h-px bg-[#E5E7EB]"></div>
            <div class="flex justify-between items-center">
              <span class="text-[#6B7280] text-md">
                <template v-if="isInternationalOrder">
                  {{ t('print.vat-included') }}
                </template>
                <template v-else>
                  {{ t('print.sales-tax') }}
                  <span v-if="isCalculating" class="text-sm text-[gray] ml-2">
                    (calculating...)
                  </span>
                </template>
              </span>
              <span v-if="!isInternationalOrder" class="text-md">
                {{ printStore.currencySymbol
                }}{{ printStore.checkout.taxAmount.toFixed(2) }}
              </span>
            </div>
          </template>

          <template
            v-if="
              printStore.checkout.step >= 2 &&
              printStore.checkout.processingFeeAmount > 0
            "
          >
            <div class="h-px bg-[#E5E7EB]"></div>
            <div class="flex justify-between items-center">
              <span class="text-[#6B7280] text-md">
                Processing Fee
                <span v-if="processingFeeRate > 0" class="text-sm">
                  ({{ (processingFeeRate * 100).toFixed(1) }}%)
                </span>
              </span>
              <span class="text-md">
                {{ printStore.currencySymbol
                }}{{ printStore.checkout.processingFeeAmount.toFixed(2) }}
              </span>
            </div>
          </template>

          <template v-if="printStore.checkout.step >= 2">
            <div class="h-px bg-[#E5E7EB]"></div>
            <div class="flex justify-between items-center">
              <span class="text-[#6B7280] text-md">Shipping and Handling</span>
              <span class="text-md"
                >{{ printStore.currencySymbol
                }}{{
                  (
                    (printStore.checkout.shippingRate?.amount || 0) +
                    printStore.checkout.flatFeeAmount
                  ).toFixed(2)
                }}</span
              >
            </div>
          </template>

          <div class="h-px bg-[#E5E7EB]"></div>
          <div class="flex justify-between items-center">
            <span class="text-[#6B7280] text-xl font-medium">{{
              t('print.total')
            }}</span>
            <span class="text-xl font-medium">
              {{ printStore.currencySymbol
              }}{{
                (
                  printStore.subtotal +
                  (printStore.checkout.step >= 2
                    ? printStore.checkout.shippingRate?.amount || 0
                    : 0) +
                  (printStore.checkout.step === 3 && !isInternationalOrder
                    ? printStore.checkout.taxAmount
                    : 0) +
                  printStore.checkout.flatFeeAmount +
                  printStore.checkout.processingFeeAmount
                ).toFixed(2)
              }}
            </span>
          </div>
          <div v-if="printStore.checkout.step === 1" class="text-sm w-1/3">
            <span class="text-[#6B7280]"
              >Tax and shipping costs will be calculated at final checkout</span
            >
          </div>
        </div>
      </div>

      <div class="w-full bg-white p-4 lg:pt-4 border-gray">
        <Footer />
      </div>
    </div>

    <div class="w-full lg:w-[53%] bg-[#F8FAFC] p-4 lg:p-8">
      <div class="flex justify-center mb-4 lg:mb-4">
        <div class="flex items-center gap-2 lg:gap-4 text-sm lg:text-base">
          <span
            :class="[
              printStore.checkout.step >= 1 ? 'text-primary' : 'text-[gray]',
            ]"
            class="font-medium"
          >
            {{ t('print.shipping') }}
          </span>
          <hr
            class="w-6"
            :class="[printStore.checkout.step > 1 ? 'bg-primary' : 'bg-[gray]']"
          />
          <Icon
            :icon="CheckCircleSvg"
            class="w-4 h-4 lg:w-5 lg:h-5"
            :class="[
              printStore.checkout.step != 1 ? 'text-primary' : 'text-[gray]',
            ]"
            :size="IconSize.SM"
          />
          <hr
            class="w-6"
            :class="[printStore.checkout.step > 1 ? 'bg-primary' : 'bg-[gray]']"
          />
          <span
            :class="[
              printStore.checkout.step >= 2 ? 'text-primary' : 'text-[gray]',
            ]"
            class="font-medium"
          >
            {{ t('print.delivery') }}
          </span>
          <hr
            class="w-6"
            :class="[printStore.checkout.step > 2 ? 'bg-primary' : 'bg-[gray]']"
          />
          <Icon
            :icon="CheckCircleSvg"
            class="w-4 h-4 lg:w-5 lg:h-5"
            :class="[
              printStore.checkout.step > 2 ? 'text-primary' : 'text-[gray]',
            ]"
            :size="IconSize.SM"
          />
          <hr
            class="w-6"
            :class="[printStore.checkout.step > 2 ? 'bg-primary' : 'bg-[gray]']"
          />
          <span
            :class="[
              printStore.checkout.step === 3 ? 'text-primary' : 'text-[gray]',
            ]"
            class="font-medium"
          >
            {{ t('print.payment') }}
          </span>
        </div>
      </div>

      <component
        :is="currentStep"
        :print-budget-summary="printBudgetSummary"
        :can-cover-with-budget="canCoverWithBudget"
        :is-budget-exceeded="isBudgetExceeded"
        :order-total="orderTotal"
        @next="handleNextStep"
        @back="handlePreviousStep"
        @payment-success="handlePaymentSuccess"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Icon, IconSize } from '~/components/icon';
import ArrowLeftSvg from '~/assets/icons/arrow-left.svg?component';
import { usePrintStore } from '~/store/print';
import ShippingStep from './_steps/shipping-step.vue';
import PaymentStep from './_steps/payment-step.vue';
import TrashSvg from '~/assets/icons/trash.svg?component';
import CheckCircleSvg from '~/assets/icons/check-circle.svg?component';
import { useStripeTax } from '~/composables/useStripeTax';
import DeliveryStep from './_steps/delivery-step.vue';
import { Footer } from '~/components/footer';
import { SelectInput } from '~/components/inputs/select-input';
import { PrintableItem } from '../_types';
import {
  apiGetPrintBudgetSummary,
  apiGetPrintConfig,
  apiGetBusinessShippingConfig,
  type PrintBudgetSummary,
  type BusinessShippingConfigResponse,
} from '~/api/print';

const { t } = useI18n();
const printStore = usePrintStore();

const { calculateTax, isCalculating } = useStripeTax();

const printBudgetSummary = ref<PrintBudgetSummary | null>(null);
const processingFeeRate = ref(0);
const businessShippingConfig = ref<BusinessShippingConfigResponse | null>(null);

const loadPrintBudgetSummary = async () => {
  printBudgetSummary.value = await apiGetPrintBudgetSummary();
};

const loadPrintConfig = async () => {
  const config = await apiGetPrintConfig();
  processingFeeRate.value = config.processingFeeRate || 0;
};

const calculateFees = () => {
  let totalFlatFee = 0;
  const breakdown: Array<{
    productId: number;
    productName: string;
    feeAmount: number;
    feeLabel: string;
  }> = [];

  const inventoryItemIds = businessShippingConfig.value?.inventoryItemIds || [];

  for (const [key, item] of Object.entries(printStore.selectedItems)) {
    const quantity = printStore.quantities[key] || 0;

    if (quantity <= 0 || !(item as any).printProduct) {
      continue;
    }

    const product = (item as any).printProduct;
    if (!product.fees || !Array.isArray(product.fees)) {
      continue;
    }

    if (
      product.contentFilePrintId &&
      inventoryItemIds.includes(product.contentFilePrintId)
    ) {
      continue;
    }

    for (const fee of product.fees) {
      if (fee.amount && fee.amount > 0) {
        totalFlatFee += fee.amount;
        breakdown.push({
          productId: product.id,
          productName: product.description,
          feeAmount: fee.amount,
          feeLabel: fee.label,
        });
      }
    }
  }

  let processingFeeAmount = 0;
  if (processingFeeRate.value > 0) {
    const { subtotal } = printStore;
    const shipping = printStore.checkout.shippingRate?.amount || 0;
    const tax = printStore.checkout.taxAmount || 0;

    const orderTotalBeforeFee = subtotal + shipping + tax + totalFlatFee;

    const totalWithProcessingFee =
      orderTotalBeforeFee / (1 - processingFeeRate.value);
    processingFeeAmount =
      Math.round((totalWithProcessingFee - orderTotalBeforeFee) * 100) / 100;
  }

  printStore.setFees({
    flatFeeAmount: totalFlatFee,
    flatFeeBreakdown: breakdown,
    processingFeeAmount,
  });
};

const isInternationalOrder = computed(() => {
  return printStore.checkout.shippingAddress?.address?.country !== 'US';
});

const orderTotal = computed(() => {
  const { subtotal } = printStore;
  const shippingAmount =
    printStore.checkout.step >= 2
      ? printStore.checkout.shippingRate?.amount || 0
      : 0;
  const taxAmount =
    printStore.checkout.step === 3 && !isInternationalOrder.value
      ? printStore.checkout.taxAmount
      : 0;
  const flatFeeAmount = printStore.checkout.flatFeeAmount || 0;
  const processingFeeAmount = printStore.checkout.processingFeeAmount || 0;
  const total =
    subtotal + shippingAmount + taxAmount + flatFeeAmount + processingFeeAmount;

  return total;
});

const budgetRemaining = computed(() => {
  if (!printBudgetSummary.value) return 0;
  return printBudgetSummary.value.totalBalance - orderTotal.value * 100;
});

const isBudgetExceeded = computed(() => {
  return budgetRemaining.value < 0;
});

const canCoverWithBudget = computed(() => {
  if (!printBudgetSummary.value) return false;
  return budgetRemaining.value >= 0;
});

const currentStep = computed(() => {
  switch (printStore.checkout.step) {
    case 1: {
      return ShippingStep;
    }
    case 2: {
      return DeliveryStep;
    }
    case 3: {
      return PaymentStep;
    }
    default: {
      return ShippingStep;
    }
  }
});

const paymentSuccessful = ref(false);

const handleNextStep = async () => {
  if (printStore.checkout.step === 2) {
    const isInternational =
      printStore.checkout.shippingAddress.address.country !== 'US';

    let taxAmount = 0;

    if (!isInternational) {
      taxAmount = await calculateTax({
        subtotal: printStore.subtotal,
        shippingAmount: printStore.checkout.shippingRate?.amount || 0,
        address: printStore.checkout.shippingAddress.address,
      });
    }

    printStore.setTaxAmount(taxAmount);
    printStore.setCheckoutStep(3);
  }
};

const handlePreviousStep = () => {
  if (printStore.checkout.step > 1) {
    printStore.setCheckoutStep(printStore.checkout.step - 1);
  }
};

const getItemTiers = (item: PrintableItem) => {
  return [...(item.printProduct.priceTiers || [])].sort(
    (a, b) => a.quantity - b.quantity,
  );
};

const removeItem = (key: string) => {
  printStore.updateQuantity(key, 0);

  if (!printStore.hasItems) {
    navigateTo('/print');
  }
};

const updateQuantity = (key: string, quantity: number) => {
  if (quantity === 0) {
    removeItem(key);
  } else {
    printStore.updateQuantity(key, quantity);
  }
};

const handleBackToPrint = () => {
  printStore.resetCheckout();
  navigateTo('/print');
};

const handlePaymentSuccess = () => {
  paymentSuccessful.value = true;
};

const getQuantityOptions = (item: PrintableItem) => {
  const tiers = getItemTiers(item);
  return [
    { quantity: 0, displayText: 'Clear Selection', pricePerUnit: 0 },
    ...tiers.map((tier) => ({
      quantity: tier.quantity,
      displayText: tier.quantity.toString(),
      pricePerUnit: tier.pricePerUnit,
    })),
  ];
};

onMounted(async () => {
  printStore.initCurrency();
  loadPrintBudgetSummary();
  await loadPrintConfig();
  businessShippingConfig.value = await apiGetBusinessShippingConfig();

  if (printStore.checkout.step >= 2) {
    calculateFees();
  }
});

watch(
  () => printStore.checkout.step,
  (newStep) => {
    if (newStep === 2) {
      calculateFees();
    }
  },
);

watch(
  () => printStore.checkout.shippingRate,
  () => {
    if (printStore.checkout.step >= 2 && processingFeeRate.value > 0) {
      calculateFees();
    }
  },
  { deep: true },
);
</script>

<style scoped>
@reference "../../../assets/css/main.css";

hr {
  @apply bg-[primary]!;
  border: none;
  height: 1px;
}

.print-select {
  :deep(.input-area) {
    @apply rounded-lg!;
    @apply bg-[white]!;
    @apply border-[gray]!;
    @apply w-full!;
  }

  :deep(input) {
    @apply bg-[white]!;
    @apply placeholder-[gray]!;
    @apply text-[gray]!;
    @apply w-full!;
  }

  :deep(.options-area) {
    @apply rounded-lg!;
    @apply mt-1!;
    @apply shadow-lg!;
    @apply bg-[white]!;
    @apply border-[gray]!;
    @apply w-full!;
  }

  :deep(.options-area-item) {
    @apply hover:bg-[gray]!;
    @apply rounded-none!;
    @apply text-[gray]!;
  }

  :deep(.options-area-item.option-selected) {
    @apply bg-[gray]!;
  }
}
</style>
